import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import JumpstartStudio from '../../components/coaching/JumpstartStudio';
import NonprofitGuide from './NonprofitGuide';
import IncorporationQuestionnaire from './IncorporationQuestionnaire';
import BoardPacketModal from './BoardPacketModal';
import {
  Sparkles, CheckCircle2, Circle, Plus, Trash2, RefreshCw, Download,
  ChevronLeft, Rocket, Coffee, ClipboardList, FileText, Mic, Square, Folder, Upload, Wand2, Users,
  Map as MapIcon, CalendarDays, BookOpen, Palette, ChevronDown, Database, Info, Copy, Send, FileCheck2,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../../components/ui/collapsible';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

// Pre-fill sample drawn from GreaterCommunityImpact.org (edit as needed).
const GCI_PREFILL = {
  name: 'Greater Community Impact (GCI)',
  mission: 'Strengthen communities, mobilize volunteers, and advance economic opportunity across Savannah, GA and Southeast Georgia through workforce development, health, housing, and collaboration programs.',
  vision: 'Stronger, more resilient communities across Savannah and Southeast Georgia where individuals and families have the support they need to thrive.',
  who_served: 'Individuals and families in Savannah & Southeast Georgia seeking employment and economic stability, volunteers wanting to serve, and partner organizations/nonprofits.',
  focus_areas: 'Workforce & Education; Health & Wellness; Volunteer Mobilization; Community Collaboration (plus Housing Support).',
  community: 'Savannah, GA and Southeast Georgia / Coastal Empire.',
  funding: 'Donations, grants, employer/partner sponsorships, program income, and community events.',
  notes: 'Flagship: Workforce Development & Economic Mobility (job fairs, resume/interview workshops, GED pathways, employer partnerships, career coaching, cohorts). Volunteer network + Community Impact Calendar. Partners include United Way of the Coastal Empire, Goodwill SE GA, WorkSource Coastal, EOA, Shelter From the Rain.',
};

const EMPTY = { name: '', mission: '', vision: '', who_served: '', focus_areas: '', community: '', funding: '', notes: '' };

// Lightweight, dependency-free renderer that gives AI draft text real structure
// (headings, bullet lists, numbered lists, paragraphs) instead of a flat block.
function FormattedDraft({ text }) {
  if (!text) return null;
  const lines = String(text).replace(/\r/g, '').split('\n');
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const t = line.trim();
    if (!t) { flush(); return; }
    const mNum = t.match(/^(\d+)[.)]\s+(.*)$/);
    const mBul = t.match(/^[-*•]\s+(.*)$/);
    // Heading heuristics: markdown #, ALL-CAPS short line, or a short "Label:" line
    const isMdH = /^#{1,4}\s+/.test(t);
    const isCapsH = t.length <= 60 && /^[A-Z0-9][A-Z0-9 &/,'()-]+$/.test(t) && !mNum && !mBul;
    const isColonH = t.length <= 60 && /^[A-Z][\w &/'-]{1,50}:$/.test(t);
    if (isMdH || isCapsH || isColonH) {
      flush();
      const clean = t.replace(/^#{1,4}\s+/, '').replace(/:$/, '');
      blocks.push({ type: 'h', text: clean, key: `h${i}` });
      return;
    }
    if (mBul) {
      if (!list || list.ordered) { flush(); list = { type: 'ul', ordered: false, items: [], key: `ul${i}` }; }
      list.items.push(mBul[1]);
      return;
    }
    if (mNum) {
      if (!list || !list.ordered) { flush(); list = { type: 'ul', ordered: true, items: [], key: `ol${i}` }; }
      list.items.push(mNum[2]);
      return;
    }
    flush();
    blocks.push({ type: 'p', text: t, key: `p${i}` });
  });
  flush();
  const renderInline = (s) => {
    // bold **text** support
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, idx) => p.startsWith('**') && p.endsWith('**')
      ? <strong key={idx} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <React.Fragment key={idx}>{p}</React.Fragment>);
  };
  return (
    <div className="space-y-3" data-testid="npa-ai-draft-formatted">
      {blocks.map((b) => {
        if (b.type === 'h') return <h4 key={b.key} className="text-sm font-bold text-[#1D4ED8] tracking-tight mt-4 first:mt-0">{b.text}</h4>;
        if (b.type === 'ul') return (
          <ul key={b.key} className="space-y-1.5">
            {b.items.map((it, idx) => (
              <li key={idx} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                {b.ordered
                  ? <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-fuchsia-100 text-fuchsia-700 text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
                  : <CheckCircle2 size={15} className="shrink-0 mt-0.5 text-fuchsia-500" />}
                <span>{renderInline(it)}</span>
              </li>
            ))}
          </ul>
        );
        return <p key={b.key} className="text-sm text-slate-700 leading-relaxed">{renderInline(b.text)}</p>;
      })}
    </div>
  );
}


const TYPE_META = {
  task: { icon: ClipboardList, color: '#1D4ED8', label: 'Task' },
  review: { icon: RefreshCw, color: '#059669', label: 'Review' },
  brain_break: { icon: Coffee, color: '#D97706', label: 'Brain Break' },
  launch: { icon: Rocket, color: '#DB2777', label: 'Launch' },
};

export default function NonprofitAccelerator() {
  const [projects, setProjects] = useState([]);
  const [mode, setMode] = useState('accelerator');
  const [active, setActive] = useState(null); // full project + curriculum
  const [curriculum, setCurriculum] = useState([]);
  const [phases, setPhases] = useState([]);
  const [planView, setPlanView] = useState('roadmap'); // roadmap | calendar | list
  const [selectedDay, setSelectedDay] = useState(1);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [dayNotes, setDayNotes] = useState('');
  const [savingDay, setSavingDay] = useState(false);
  const [drafting, setDrafting] = useState(false);
  // Library
  const [library, setLibrary] = useState({ folders: [], templates: [] });
  const [uploadFolder, setUploadFolder] = useState('Board');
  const [newFolder, setNewFolder] = useState('');
  const [uploading, setUploading] = useState(false);
  // AI intake (type/speak)
  const [intakeText, setIntakeText] = useState('');
  const [intaking, setIntaking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState([]);
  // Board recruitment tracker
  const [suggesting, setSuggesting] = useState(false);
  const [newProspect, setNewProspect] = useState({ name: '', skill: '', status: 'prospect', notes: '' });
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteLoading, setWebsiteLoading] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [draftingAll, setDraftingAll] = useState(false);
  const [draftAllProgress, setDraftAllProgress] = useState({ completed: 0, total: 0, current_day: null, current_title: null });
  const [npaSessions, setNpaSessions] = useState([]);
  const [syncSessionId, setSyncSessionId] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [blueprintLoading, setBlueprintLoading] = useState(false);
  const [showBlueprint, setShowBlueprint] = useState(false);
  const [showPacket, setShowPacket] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [showReadiness, setShowReadiness] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [activity, setActivity] = useState([]);
  const [cmpVer, setCmpVer] = useState(null);
  // AI Brand Refresh
  const [brandLoading, setBrandLoading] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [brandOptions, setBrandOptions] = useState(null);
  const [brandSel, setBrandSel] = useState({});
  const [applyingBrand, setApplyingBrand] = useState(false);
  const audioFileRef = React.useRef(null);
  const restoreInputRef = React.useRef(null);
  const [showHelp, setShowHelp] = useState(false);

  // Poll a background job (draft/autofill/blueprint) until done/error.
  const pollJob = (jobId, onDone, onErr) => {
    let attempts = 0;
    let transient = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const jr = await api.get(`/api/admin/nonprofit-accelerator/draft-jobs/${jobId}`);
        transient = 0;
        if (jr.data.status === 'done') onDone(jr.data);
        else if (jr.data.status === 'error') (onErr || (() => {}))(jr.data.error || 'failed');
        else if (attempts > 60) (onErr || (() => {}))('timed out');
        else setTimeout(tick, 3000);
      } catch (e) {
        // Tolerate transient gateway blips (502/504) during long AI jobs; only give up after several in a row.
        transient += 1;
        if (transient >= 5) (onErr || (() => {}))(e.response?.data?.detail || e.message);
        else setTimeout(tick, 3000);
      }
    };
    setTimeout(tick, 3000);
  };
  const mediaRef = React.useRef(null);
  const chunksRef = React.useRef([]);

  const refreshLibrary = async () => {
    try {
      const r = await api.get('/api/admin/nonprofit-accelerator/library');
      setLibrary(r.data);
      if (r.data.folders?.length && !r.data.folders.includes(uploadFolder)) setUploadFolder(r.data.folders[0]);
    } catch (e) { /* silent */ }
  };

  const refreshProjects = async () => {
    try {
      const r = await api.get('/api/admin/nonprofit-accelerator/projects');
      setProjects(r.data.projects || []);
    } catch (e) { toast.error('Failed to load projects'); }
  };

  useEffect(() => { refreshProjects(); refreshLibrary(); }, []);

  const openProject = async (id) => {
    try {
      const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${id}`);
      setActive(r.data.project);
      setCurriculum(r.data.curriculum || []);
      setPhases(r.data.phases || []);
      setReadiness(r.data.readiness || null);
      setBrandOptions(r.data.project.brand_options || null);
      setBrandSel({});
      setSelectedDay(1);
      const e = (r.data.project.days || {})['1'] || {};
      setDayNotes(e.notes || '');
      try {
        const sr = await api.get('/api/admin/nonprofit-accelerator/sessions');
        setNpaSessions(sr.data.sessions || []);
      } catch (_) { setNpaSessions([]); }
    } catch (e) { toast.error('Open failed'); }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Give your nonprofit a name'); return; }
    setCreating(true);
    try {
      const r = await api.post('/api/admin/nonprofit-accelerator/projects', form);
      toast.success('Project created');
      setForm(EMPTY);
      await refreshProjects();
      openProject(r.data.project.project_id);
    } catch (e) {
      toast.error('Create failed: ' + (e.response?.data?.detail || e.message));
    } finally { setCreating(false); }
  };

  // Create a project directly from a website or a recording (main page, no manual typing).
  const [createUrl, setCreateUrl] = useState('');
  const [createFromLoading, setCreateFromLoading] = useState('');
  const createStartFileRef = React.useRef(null);

  const createFromWebsite = async () => {
    if (!createUrl.trim()) { toast.error('Enter a website URL'); return; }
    setCreateFromLoading('web');
    try {
      let host = createUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      const cr = await api.post('/api/admin/nonprofit-accelerator/projects', { name: host || 'New nonprofit' });
      const pid = cr.data.project.project_id;
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${pid}/ai-intake-website`, { url: createUrl });
      toast.success(`Project created from website - filled ${r.data.updated_fields?.length || 0} field(s)`);
      setCreateUrl('');
      await refreshProjects();
      openProject(pid);
    } catch (e) {
      toast.error('Website analyze failed: ' + (e.response?.data?.detail || e.message));
    } finally { setCreateFromLoading(''); }
  };

  const createFromRecording = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCreateFromLoading('audio');
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      const tr = await api.post('/api/admin/nonprofit-accelerator/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const txt = (tr.data.text || '').trim();
      if (!txt) { toast.error('No speech detected in recording'); return; }
      const cr = await api.post('/api/admin/nonprofit-accelerator/projects', { name: 'New nonprofit (from recording)' });
      const pid = cr.data.project.project_id;
      const ir = await api.post(`/api/admin/nonprofit-accelerator/projects/${pid}/ai-intake`, { text: txt });
      toast.success(`Project created from recording - filled ${ir.data.updated_fields?.length || 0} field(s)`);
      await refreshProjects();
      openProject(pid);
    } catch (err) {
      toast.error('Recording analyze failed: ' + (err.response?.data?.detail || err.message));
    } finally { setCreateFromLoading(''); e.target.value = ''; }
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Delete this nonprofit project?')) return;
    await api.delete(`/api/admin/nonprofit-accelerator/projects/${id}`);
    if (active?.project_id === id) setActive(null);
    refreshProjects();
    toast.success('Deleted');
  };

  const selectDay = (day) => {
    setSelectedDay(day);
    const e = (active.days || {})[String(day)] || {};
    setDayNotes(e.notes || '');
  };

  const spec = curriculum.find((d) => d.day === selectedDay) || {};
  const dayEntry = (active?.days || {})[String(selectedDay)] || {};

  const saveDay = async (extra = {}) => {
    setSavingDay(true);
    try {
      const r = await api.patch(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/days/${selectedDay}`, { notes: dayNotes, ...extra });
      setActive((cur) => ({ ...cur, days: { ...(cur.days || {}), [String(selectedDay)]: r.data.entry } }));
      if (!('silent' in extra)) toast.success('Saved');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSavingDay(false); }
  };

  const toggleComplete = () => saveDay({ completed: !dayEntry.completed });

  const handleAiDraft = async () => {
    setDrafting(true);
    const day = selectedDay;
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/days/${day}/ai-draft`);
      pollJob(r.data.job_id, (data) => {
        setActive((cur) => {
          const e = { ...((cur.days || {})[String(day)] || {}), ai_draft: data.ai_draft };
          return { ...cur, days: { ...(cur.days || {}), [String(day)]: e } };
        });
        toast.success('AI draft ready');
        setDrafting(false);
      }, (err) => { toast.error('AI draft failed: ' + err); setDrafting(false); });
    } catch (e) {
      toast.error('AI draft failed: ' + (e.response?.data?.detail || e.message));
      setDrafting(false);
    }
  };

  const downloadPlan = () => {
    window.open(`${API_URL}/api/admin/nonprofit-accelerator/projects/${active.project_id}/launch-plan`, '_blank');
  };

  const refreshActive = async () => {
    try { const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${active.project_id}`); setActive(r.data.project); setReadiness(r.data.readiness || null); } catch (_) {}
  };

  const READINESS_DAY = { mission: 31, programs: 38, bylaws: 15, budget: 53, roster: 12 };
  const openActivity = async () => {
    setShowActivity(true); setActivity([]);
    try { const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/activity`); setActivity(r.data.activity || []); }
    catch { toast.error('Could not load activity'); }
  };
  const exportBackup = () => { window.open(`${API_URL}/api/admin/nonprofit-accelerator/projects/${active.project_id}/backup`, '_blank'); };
  const importBackup = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!window.confirm('Restore this project from the backup file? This overwrites the current days and fields (a snapshot is kept).')) { e.target.value = ''; return; }
    try {
      const data = JSON.parse(await f.text());
      await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/restore-backup`, { project: data.project, day_versions: data.day_versions });
      await refreshActive(); toast.success('Project restored from backup');
    } catch (err) { toast.error('Restore failed: ' + (err.response?.data?.detail || err.message)); }
    finally { e.target.value = ''; }
  };

  const openVersions = async () => {
    setShowVersions(true); setVersions([]);
    try { const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/days/${selectedDay}/versions`); setVersions(r.data.versions || []); }
    catch { toast.error('Could not load history'); }
  };
  const restoreVersion = async (vid) => {
    try {
      await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/days/${selectedDay}/versions/${vid}/restore`);
      await refreshActive();
      const e = ((active.days || {})[String(selectedDay)]) || {};
      setDayNotes(e.notes || '');
      toast.success('Restored - your current version was saved to history first');
      setShowVersions(false);
    } catch { toast.error('Restore failed'); }
  };

  const uploadDayAttachment = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    toast.info('Uploading…');
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/days/${selectedDay}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshActive();
      toast.success(r.data.has_text ? 'File added - AI draft will read & enhance it' : 'File added (no readable text extracted)');
    } catch (err) { toast.error('Upload failed: ' + (err.response?.data?.detail || err.message)); }
    finally { e.target.value = ''; }
  };

  const deleteDayAttachment = async (attId) => {
    try { await api.delete(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/days/${selectedDay}/attachments/${attId}`); await refreshActive(); toast.success('Removed'); }
    catch { toast.error('Could not remove'); }
  };

  const downloadPdf = async (kind) => {
    const map = { plan: 'plan-pdf', packet: 'board-packet-pdf' };
    const label = kind === 'packet' ? 'board packet' : '90-day plan';
    toast.info(`Preparing your ${label} PDF…`);
    try {
      const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/${map[kind]}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${(active.name || 'nonprofit').replace(/\s+/g, '_')}_${kind === 'packet' ? 'board_packet' : '90_day_plan'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // ---- Library ----
  const handleCreateFolder = async () => {
    if (!newFolder.trim()) return;
    await api.post('/api/admin/nonprofit-accelerator/folders', { name: newFolder.trim() });
    setUploadFolder(newFolder.trim());
    setNewFolder('');
    refreshLibrary();
    toast.success('Folder created');
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('folder', uploadFolder || 'Uncategorized');
      await api.post('/api/admin/nonprofit-accelerator/templates', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Template uploaded');
      refreshLibrary();
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Remove this template from the library?')) return;
    await api.delete(`/api/admin/nonprofit-accelerator/templates/${id}`);
    refreshLibrary();
    toast.success('Removed');
  };

  const downloadTemplate = async (t) => {
    try {
      const r = await api.get(`/api/admin/nonprofit-accelerator/templates/${t.template_id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = t.original_filename || (t.name + '.docx');
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  // ---- AI intake (type / speak) ----
  const handleIntake = async () => {
    if (!intakeText.trim()) { toast.error('Type or record something first'); return; }
    setIntaking(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/ai-intake`, { text: intakeText });
      setActive((cur) => ({ ...cur, ...r.data.project }));
      setLastUpdated(r.data.updated_fields || []);
      setIntakeText('');
      toast.success(`Updated ${r.data.updated_fields?.length || 0} field(s) from your description`);
    } catch (e) {
      toast.error('AI intake failed: ' + (e.response?.data?.detail || e.message));
    } finally { setIntaking(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, 'voice.webm');
          const r = await api.post('/api/admin/nonprofit-accelerator/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const txt = (r.data.text || '').trim();
          if (txt) { setIntakeText((cur) => (cur ? cur + ' ' : '') + txt); toast.success('Transcribed'); }
          else toast.error('No speech detected');
        } catch (e) {
          toast.error('Transcription failed: ' + (e.response?.data?.detail || e.message));
        } finally { setTranscribing(false); }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error('Microphone access denied or unavailable');
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); }
  };

  // ---- Board recruitment tracker ----
  const prospects = active?.board_prospects || [];
  const addProspect = async () => {
    if (!newProspect.name.trim() && !newProspect.skill.trim()) { toast.error('Add a name or a skill'); return; }
    const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/prospects`, newProspect);
    setActive((cur) => ({ ...cur, board_prospects: [...(cur.board_prospects || []), r.data.prospect] }));
    setNewProspect({ name: '', skill: '', status: 'prospect', notes: '' });
    toast.success('Prospect added');
  };
  const updateProspect = async (id, patch) => {
    setActive((cur) => ({ ...cur, board_prospects: (cur.board_prospects || []).map((p) => p.prospect_id === id ? { ...p, ...patch } : p) }));
    try { await api.patch(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/prospects/${id}`, patch); }
    catch (e) { toast.error('Update failed'); }
  };
  const deleteProspect = async (id) => {
    await api.delete(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/prospects/${id}`);
    setActive((cur) => ({ ...cur, board_prospects: (cur.board_prospects || []).filter((p) => p.prospect_id !== id) }));
  };
  const suggestProspects = async () => {
    setSuggesting(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/prospects/ai-suggest`);
      setActive((cur) => ({ ...cur, board_prospects: r.data.prospects }));
      toast.success(`Added ${r.data.added?.length || 0} suggested profiles`);
    } catch (e) {
      toast.error('Suggest failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSuggesting(false); }
  };
  const PROSPECT_STATUSES = ['prospect', 'invited', 'agreed', 'onboarded', 'declined'];
  const STATUS_COLORS = { prospect: 'bg-slate-100 text-slate-600', invited: 'bg-amber-100 text-amber-700', agreed: 'bg-blue-100 text-blue-700', onboarded: 'bg-emerald-100 text-emerald-700', declined: 'bg-red-100 text-red-600' };

  // ---- Website / audio-upload / autofill / blueprint ----
  const handleWebsiteIntake = async () => {
    if (!websiteUrl.trim()) { toast.error('Enter a website URL'); return; }
    setWebsiteLoading(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/ai-intake-website`, { url: websiteUrl });
      setActive((cur) => ({ ...cur, ...r.data.project }));
      setLastUpdated(r.data.updated_fields || []);
      toast.success(`Pulled from website - updated ${r.data.updated_fields?.length || 0} field(s)`);
    } catch (e) {
      toast.error('Website pull failed: ' + (e.response?.data?.detail || e.message));
    } finally { setWebsiteLoading(false); }
  };

  const handleAudioUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      const r = await api.post('/api/admin/nonprofit-accelerator/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const txt = (r.data.text || '').trim();
      if (txt) { setIntakeText((cur) => (cur ? cur + ' ' : '') + txt); toast.success('Recording transcribed'); }
      else toast.error('No speech detected');
    } catch (err) {
      toast.error('Transcription failed: ' + (err.response?.data?.detail || err.message));
    } finally { setTranscribing(false); e.target.value = ''; }
  };

  const handleAutofillDays = async () => {
    if (!intakeText.trim()) { toast.error('Add notes or a recording first'); return; }
    setAutofilling(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/autofill-days`, { text: intakeText });
      pollJob(r.data.job_id, async (data) => {
        await openProject(active.project_id);
        toast.success(`Auto-filled ${data.updated_days?.length || 0} day(s) across your plan`);
        setAutofilling(false);
      }, (err) => { toast.error('Auto-fill failed: ' + err); setAutofilling(false); });
    } catch (e) {
      toast.error('Auto-fill failed: ' + (e.response?.data?.detail || e.message));
      setAutofilling(false);
    }
  };

  // Poll a draft-all job with progress updates (long-running; server keeps drafting even if you leave).
  const pollDraftAll = (jobId) => {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const jr = await api.get(`/api/admin/nonprofit-accelerator/draft-jobs/${jobId}`);
        const d = jr.data;
        setDraftAllProgress({ completed: d.completed || 0, total: d.total || 0, current_day: d.current_day, current_title: d.current_title });
        if (d.status === 'done') {
          setDraftingAll(false);
          await openProject(active.project_id);
          toast.success(`AI drafted ${(d.drafted_days || []).length} day(s). Open any day to review.`);
        } else if (d.status === 'error') {
          setDraftingAll(false);
          toast.error('AI-draft all failed: ' + (d.error || 'unknown'));
        } else if (attempts > 2000) {
          setDraftingAll(false);
        } else {
          setTimeout(tick, 3000);
        }
      } catch (e) {
        if (attempts > 2000) { setDraftingAll(false); } else { setTimeout(tick, 4000); }
      }
    };
    setTimeout(tick, 2000);
  };

  const handleDraftAll = async () => {
    if (draftingAll) return;
    if (!window.confirm('AI-draft every task day that isn\'t drafted yet? This can take several minutes and uses AI credits. Days you\'ve already drafted are skipped, and your typed notes are left untouched.')) return;
    setDraftingAll(true);
    setDraftAllProgress({ completed: 0, total: 0, current_day: null, current_title: null });
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/ai-draft-all`);
      if (!r.data.total) {
        setDraftingAll(false);
        toast.success('All task days are already AI-drafted. Nothing to do!');
        return;
      }
      setDraftAllProgress({ completed: 0, total: r.data.total, current_day: null, current_title: null });
      toast.success(`Drafting ${r.data.total} days in the background…`);
      pollDraftAll(r.data.job_id);
    } catch (e) {
      setDraftingAll(false);
      toast.error('Could not start: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleSyncFromSession = async () => {
    if (!syncSessionId) { toast.error('Pick a 7-Stage session first'); return; }
    setSyncing(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/sync-from-session`, { session_id: syncSessionId });
      pollJob(r.data.job_id, async (data) => {
        await openProject(active.project_id);
        toast.success(`Pre-filled ${data.updated_days?.length || 0} day(s) from your 7-Stage session`);
        setSyncing(false);
      }, (err) => { toast.error('Sync failed: ' + err); setSyncing(false); });
    } catch (e) {
      toast.error('Sync failed: ' + (e.response?.data?.detail || e.message));
      setSyncing(false);
    }
  };

  const handleGenerateBlueprint = async () => {
    setBlueprintLoading(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/generate-blueprint`);
      pollJob(r.data.job_id, (data) => {
        setActive((cur) => ({ ...cur, blueprint: data.blueprint }));
        setShowBlueprint(true);
        toast.success('Launch Blueprint ready');
        setBlueprintLoading(false);
      }, (err) => { toast.error('Blueprint failed: ' + err); setBlueprintLoading(false); });
    } catch (e) {
      toast.error('Blueprint failed: ' + (e.response?.data?.detail || e.message));
      setBlueprintLoading(false);
    }
  };

  const BRAND_LABELS = { mission: 'Mission', vision: 'Vision', tagline: 'Tagline', elevator_pitch: 'Elevator pitch' };
  const handleBrandRefresh = async () => {
    if (brandLoading) return;
    // If we already have options, just reopen the picker.
    if (brandOptions) { setShowBrand(true); return; }
    setBrandLoading(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/brand-refresh`);
      toast.success('Generating community-first wording options in the background…');
      pollJob(r.data.job_id, (data) => {
        setBrandOptions(data.options || {});
        setBrandSel({});
        setShowBrand(true);
        setBrandLoading(false);
        toast.success('Brand Refresh options ready');
      }, (err) => { toast.error('Brand Refresh failed: ' + err); setBrandLoading(false); });
    } catch (e) {
      toast.error('Could not start: ' + (e.response?.data?.detail || e.message));
      setBrandLoading(false);
    }
  };

  const applyBrand = async () => {
    const payload = {};
    Object.entries(brandSel).forEach(([field, val]) => { if (val) payload[field] = val; });
    if (!Object.keys(payload).length) { toast.error('Pick at least one option to apply'); return; }
    setApplyingBrand(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/brand-refresh/apply`, payload);
      setActive(r.data.project);
      setShowBrand(false);
      toast.success('Applied. Your original wording is saved and can be reverted.');
    } catch (e) {
      toast.error('Apply failed: ' + (e.response?.data?.detail || e.message));
    } finally { setApplyingBrand(false); }
  };

  const revertBrand = async () => {
    if (!window.confirm('Revert Mission / Vision / Tagline / Elevator pitch to the version before the last Brand Refresh?')) return;
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/brand-refresh/revert`);
      setActive(r.data.project);
      toast.success('Reverted to the previous wording');
    } catch (e) {
      toast.error('Revert failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const [voiceMatching, setVoiceMatching] = useState(false);
  const matchVoiceDeliverables = async () => {
    if (voiceMatching) return;
    if (!window.confirm('Rewrite your landing-page copy (Day 36) and leadership bio (Day 59) so their tone matches your chosen brand voice? Previous drafts are snapshotted and can be restored. Uses AI credits.')) return;
    setVoiceMatching(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${active.project_id}/brand-refresh/apply-to-deliverables`);
      toast.success('Refreshing your landing page & bio to match the voice…');
      pollJob(r.data.job_id, async (data) => {
        await openProject(active.project_id);
        toast.success(`Refreshed ${data.updated_days?.length || 0} deliverable(s) to match your brand voice`);
        setVoiceMatching(false);
      }, (err) => { toast.error('Voice match failed: ' + err); setVoiceMatching(false); });
    } catch (e) {
      toast.error('Could not start: ' + (e.response?.data?.detail || e.message));
      setVoiceMatching(false);
    }
  };
  const downloadVoiceGuide = () => {
    window.open(`${API_URL}/api/admin/nonprofit-accelerator/projects/${active.project_id}/brand-voice-guide-pdf`, '_blank');
  };

  const renameFolder = async (folder) => {
    const nn = window.prompt(`Rename folder "${folder}" to:`, folder);
    if (!nn || nn.trim() === folder) return;
    await api.patch(`/api/admin/nonprofit-accelerator/folders/${encodeURIComponent(folder)}`, { new_name: nn.trim() });
    if (uploadFolder === folder) setUploadFolder(nn.trim());
    refreshLibrary();
    toast.success('Folder renamed');
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder}" and all its templates?`)) return;
    await api.delete(`/api/admin/nonprofit-accelerator/folders/${encodeURIComponent(folder)}`);
    refreshLibrary();
    toast.success('Folder deleted');
  };


  const completedCount = active ? Object.values(active.days || {}).filter((v) => v.completed).length : 0;

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="nonprofit-accelerator-page">
        <div className="flex items-center gap-2 mb-1">
          <Rocket size={22} className="text-[#DB2777]" aria-hidden="true" />
          <h1 className="text-3xl font-black text-[#0A0F1C] tracking-tight" data-testid="npa-title">Nonprofit Launch Accelerator</h1>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Internal tool</span>
        </div>
        <p className="text-sm text-[#475569] mb-4">Nonprofits That Profit - 90 Day Launch Accelerator (Days 1-90). Walk day-by-day, capture your work, and let AI draft each deliverable.</p>

        {/* Mode toggle: 30-day accelerator vs embedded 7-Stage sessions (nonprofit context) */}
        <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-white p-1 mb-6" data-testid="npa-mode-toggle">
          <button type="button" onClick={() => setMode('accelerator')} data-testid="npa-mode-accelerator" className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md ${mode === 'accelerator' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}><Rocket size={13} /> 90-Day Accelerator</button>
          <button type="button" onClick={() => setMode('sessions')} data-testid="npa-mode-sessions" className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md ${mode === 'sessions' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}><Users size={13} /> Seven Stage Nonprofit Jumpstart</button>
          <button type="button" onClick={() => setMode('guide')} data-testid="npa-mode-guide" className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md ${mode === 'guide' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}><BookOpen size={13} /> Guide</button>
          <button type="button" onClick={() => setMode('incorporation')} data-testid="npa-mode-incorporation" className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md ${mode === 'incorporation' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}><FileCheck2 size={13} /> 501(c)(3) Q&amp;A</button>
        </div>

        {mode === 'incorporation' ? (
          active ? (
            <IncorporationQuestionnaire projectId={active.project_id} projectName={active.name} />
          ) : (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center" data-testid="npa-incorp-no-project">
              <FileCheck2 size={30} className="mx-auto text-[#1D4ED8] mb-3" />
              <h2 className="text-lg font-bold text-[#0A0F1C]">Open a project to fill its questionnaire</h2>
              <p className="text-sm text-[#64748B] mt-1 max-w-md mx-auto">The incorporation &amp; 501(c)(3) questionnaire is tied to a nonprofit project so AI can auto-answer from its data. Pick a project first.</p>
              <button type="button" onClick={() => setMode('accelerator')} data-testid="npa-incorp-goto-projects" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90"><Rocket size={14} /> Go to projects</button>
            </div>
          )
        ) : mode === 'guide' ? (
          <NonprofitGuide />
        ) : mode === 'sessions' ? (
          <div data-testid="npa-sessions-embed" className="bg-white border border-[#E2E8F0] rounded-2xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <p className="text-xs text-[#64748B] max-w-2xl">The <strong>Seven Stage Nonprofit Jumpstart</strong> — the same 7-Stage discovery tool, tuned for nonprofits. Sessions you create here are tagged <strong>nonprofit</strong> and stay in the accelerator; you can assign a finished blueprint to a client's portal, and sync it straight into a project's 90-day plan.</p>
              <div className="flex items-center gap-2 shrink-0">
                <a href="/book/nonprofit-jumpstart" target="_blank" rel="noreferrer" data-testid="npa-booking-link" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#0A0F1C] text-white rounded-lg hover:opacity-90"><CalendarDays size={13} /> Booking page</a>
                <button type="button" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/book/nonprofit-jumpstart`); toast.success('Booking link copied'); }} data-testid="npa-booking-copy" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#E2E8F0] text-[#475569] rounded-lg hover:bg-[#F8FAFC]"><Upload size={13} /> Copy link</button>
              </div>
            </div>
            <JumpstartStudio context="nonprofit" />
          </div>
        ) : !active ? (
          <>
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Create */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3" data-testid="npa-create-card">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Start a nonprofit project</h2>
                <button type="button" onClick={() => setForm(GCI_PREFILL)} data-testid="npa-prefill-gci" className="text-[11px] font-bold text-[#1D4ED8] hover:underline">Prefill with GCI sample (internal)</button>
              </div>

              {/* Quick start: build a project profile from a website or a recording */}
              <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-3 space-y-2" data-testid="npa-quick-start">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED]">Quick start - let AI build the profile</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input value={createUrl} onChange={(e) => setCreateUrl(e.target.value)} data-testid="npa-create-url" placeholder="Paste a nonprofit website URL" className="flex-1 min-w-[200px] px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  <button type="button" onClick={createFromWebsite} disabled={createFromLoading === 'web'} data-testid="npa-create-from-website" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#7C3AED] text-white rounded-lg hover:opacity-90 disabled:opacity-60">
                    {createFromLoading === 'web' ? <><RefreshCw size={13} className="animate-spin" /> Analyzing…</> : <><Wand2 size={13} /> Analyze website</>}
                  </button>
                </div>
                <label className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-white border border-[#DDD6FE] text-[#7C3AED] rounded-lg hover:bg-white/70 cursor-pointer" data-testid="npa-create-from-recording-label">
                  {createFromLoading === 'audio' ? <><RefreshCw size={13} className="animate-spin" /> Analyzing recording…</> : <><Mic size={13} /> Upload a recording & build profile</>}
                  <input ref={createStartFileRef} type="file" accept="audio/*,video/mp4,.m4a,.mp3,.wav,.webm" onChange={createFromRecording} className="hidden" data-testid="npa-create-from-recording-input" />
                </label>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] text-center">or fill in manually</div>
              {[['name', 'Nonprofit name'], ['mission', 'Mission'], ['vision', 'Vision'], ['who_served', 'Who it serves'], ['focus_areas', 'Focus areas / pillars'], ['community', 'Community served'], ['funding', 'Funding approach'], ['notes', 'Other notes']].map(([k, label]) => (
                <div key={k}>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">{label}</label>
                  {k === 'name' || k === 'community' ? (
                    <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`npa-field-${k}`} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  ) : (
                    <textarea value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`npa-field-${k}`} rows={k === 'notes' ? 3 : 2} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  )}
                </div>
              ))}
              <button type="button" onClick={handleCreate} disabled={creating} data-testid="npa-create-button" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90 disabled:opacity-60">
                {creating ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />} Create project
              </button>
            </div>

            {/* List */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3" data-testid="npa-projects-list">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Your projects</h2>
              {projects.length === 0 ? (
                <p className="text-sm text-[#64748B] py-8 text-center">No projects yet. Create one to begin.</p>
              ) : projects.map((p) => (
                <div key={p.project_id} data-testid={`npa-project-${p.project_id}`} className="flex items-center justify-between gap-2 border border-[#E2E8F0] rounded-xl p-3 hover:border-[#1D4ED8]">
                  <button type="button" onClick={() => openProject(p.project_id)} className="text-left flex-1">
                    <div className="font-semibold text-sm text-[#0A0F1C]">{p.name}</div>
                    <div className="text-xs text-[#64748B]">{p.completed_count || 0}/90 days complete</div>
                    {p.readiness && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-black px-1.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]" data-testid="npa-card-readiness">Board-ready {p.readiness.score}%</div>
                    )}
                  </button>
                  <button type="button" onClick={() => handleDelete(p.project_id)} aria-label="Delete" className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Document Template Library */}
          <div className="mt-6 bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="npa-library">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C] flex items-center gap-2"><Folder size={16} className="text-[#1D4ED8]" /> Document Template Library</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={uploadFolder} onChange={(e) => setUploadFolder(e.target.value)} data-testid="npa-upload-folder" className="px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-xs">
                  {(library.folders || []).map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90 cursor-pointer" data-testid="npa-upload-label">
                  {uploading ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />} Upload
                  <input type="file" onChange={handleUpload} className="hidden" data-testid="npa-upload-input" accept=".docx,.pdf,.doc,.txt" />
                </label>
                <div className="flex items-center gap-1">
                  <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="New folder" data-testid="npa-new-folder" className="px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-xs w-28" />
                  <button type="button" onClick={handleCreateFolder} data-testid="npa-create-folder" className="px-2 py-1.5 text-xs font-bold bg-[#F1F5F9] text-[#475569] rounded-lg">Add folder</button>
                </div>
              </div>
            </div>
            {(library.folders || []).map((folder) => {
              const items = (library.templates || []).filter((t) => (t.folder || 'Uncategorized') === folder);
              return (
                <div key={folder} className="mb-4" data-testid={`npa-folder-${folder}`}>
                  <div className="text-xs font-bold text-[#0A0F1C] mb-1.5 flex items-center gap-1.5"><Folder size={13} className="text-[#94A3B8]" /> {folder} <span className="text-[#94A3B8] font-normal">({items.length})</span>
                    <button type="button" onClick={() => renameFolder(folder)} data-testid={`npa-rename-folder-${folder}`} className="ml-1 text-[10px] font-semibold text-[#1D4ED8] hover:underline">Rename</button>
                    <button type="button" onClick={() => deleteFolder(folder)} data-testid={`npa-delete-folder-${folder}`} className="text-[10px] font-semibold text-red-500 hover:underline">Delete</button>
                  </div>
                  {items.length === 0 ? <p className="text-xs text-[#94A3B8] pl-5">Empty folder</p> : (
                    <div className="grid sm:grid-cols-2 gap-2">
                      {items.map((t) => (
                        <div key={t.template_id} data-testid={`npa-template-${t.template_id}`} className="flex items-center justify-between gap-2 border border-[#E2E8F0] rounded-lg p-2.5">
                          <div className="flex items-center gap-2 min-w-0"><FileText size={14} className="text-[#1D4ED8] shrink-0" /><span className="text-sm text-[#0A0F1C] truncate">{t.name}</span></div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button type="button" onClick={() => downloadTemplate(t)} data-testid={`npa-template-download-${t.template_id}`} aria-label="Download" className="text-[#475569] hover:text-[#1D4ED8]"><Download size={14} /></button>
                            <button type="button" onClick={() => handleDeleteTemplate(t.template_id)} aria-label="Delete" className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <p className="text-[11px] text-[#94A3B8] mt-2">AI uses these templates as reference when drafting board &amp; bylaws deliverables inside each project.</p>
          </div>
          </>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <button type="button" onClick={() => { setActive(null); refreshProjects(); }} data-testid="npa-back" className="inline-flex items-center gap-1 text-sm font-semibold text-[#475569] hover:text-[#0A0F1C]"><ChevronLeft size={16} /> All projects</button>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="flex flex-col mr-1 min-w-0">
                  <span className="text-sm font-bold text-[#0A0F1C] truncate max-w-[220px]">{active.name}</span>
                  <span className="text-[11px] text-[#64748B]">{completedCount}/90 days complete</span>
                </div>
                {readiness && (
                  <button type="button" onClick={() => setShowReadiness(true)} data-testid="npa-readiness-badge" className={`inline-flex items-center gap-1 text-[11px] font-black px-2.5 py-1 rounded-full border transition-colors ${readiness.score >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : readiness.score >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'}`}>Board-ready {readiness.score}%</button>
                )}

                {/* AI Tools menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" data-testid="npa-menu-ai" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-fuchsia-200 text-fuchsia-700 bg-fuchsia-50 rounded-lg hover:bg-fuchsia-100 transition-colors">
                      <Sparkles size={14} /> AI Tools <ChevronDown size={13} className="opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuLabel className="text-fuchsia-700">AI tools</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={active.blueprint ? () => setShowBlueprint(true) : handleGenerateBlueprint} data-testid="npa-generate-blueprint" className="flex-col items-start gap-0.5 py-2 cursor-pointer">
                      <span className="flex items-center gap-2 font-semibold text-slate-800">{blueprintLoading ? <RefreshCw size={13} className="animate-spin" /> : <Wand2 size={13} className="text-fuchsia-500" />} {active.blueprint ? 'View Launch Blueprint' : 'Generate Launch Blueprint'}</span>
                      <span className="text-[11px] text-slate-500 leading-snug">A one-page strategic overview of your whole 90-day launch.</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleBrandRefresh} data-testid="npa-brand-refresh" className="flex-col items-start gap-0.5 py-2 cursor-pointer">
                      <span className="flex items-center gap-2 font-semibold text-slate-800">{brandLoading ? <RefreshCw size={13} className="animate-spin" /> : <Palette size={13} className="text-fuchsia-500" />} {brandOptions ? 'Brand options' : 'AI Brand Refresh'}</span>
                      <span className="text-[11px] text-slate-500 leading-snug">Warm / formal / bold rewrites of your mission, vision & tagline.</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDraftAll} disabled={draftingAll} data-testid="npa-draft-all" className="flex-col items-start gap-0.5 py-2 cursor-pointer">
                      <span className="flex items-center gap-2 font-semibold text-slate-800">{draftingAll ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} className="text-fuchsia-500" />} {draftingAll ? `Drafting ${draftAllProgress.completed}/${draftAllProgress.total}…` : 'AI-draft all days'}</span>
                      <span className="text-[11px] text-slate-500 leading-snug">Auto-write every task day that isn't drafted yet (uses AI credits).</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Manage menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" data-testid="npa-menu-manage" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-[#E2E8F0] text-[#475569] bg-white rounded-lg hover:bg-[#F8FAFC] transition-colors">
                      <Database size={14} className="text-[#64748B]" /> Manage <ChevronDown size={13} className="opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuLabel>Manage & share</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { window.location.href = `/admin/nonprofit-accelerator/progress/${active.project_id}`; }} data-testid="npa-progress-link" className="flex-col items-start gap-0.5 py-2 cursor-pointer">
                      <span className="flex items-center gap-2 font-semibold text-slate-800"><ClipboardList size={13} className="text-[#1D4ED8]" /> Progress &amp; Share</span>
                      <span className="text-[11px] text-slate-500 leading-snug">A shareable progress page with a public link & QR code.</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openActivity} data-testid="npa-activity-btn" className="flex-col items-start gap-0.5 py-2 cursor-pointer">
                      <span className="flex items-center gap-2 font-semibold text-slate-800"><ClipboardList size={13} className="text-[#64748B]" /> Activity log</span>
                      <span className="text-[11px] text-slate-500 leading-snug">A timeline of every edit, draft and change on this project.</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportBackup} data-testid="npa-backup-export" className="flex-col items-start gap-0.5 py-2 cursor-pointer">
                      <span className="flex items-center gap-2 font-semibold text-slate-800"><Download size={13} className="text-[#64748B]" /> Backup (export)</span>
                      <span className="text-[11px] text-slate-500 leading-snug">Download the whole project + version history as a JSON file.</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => restoreInputRef.current?.click()} data-testid="npa-backup-import" className="flex-col items-start gap-0.5 py-2 cursor-pointer">
                      <span className="flex items-center gap-2 font-semibold text-slate-800"><Upload size={13} className="text-[#64748B]" /> Restore (import)</span>
                      <span className="text-[11px] text-slate-500 leading-snug">Load a previous JSON backup back into this project.</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Export menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" data-testid="npa-menu-export" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-[#BFDBFE] text-[#1D4ED8] bg-[#EFF6FF] rounded-lg hover:bg-[#DBEAFE] transition-colors">
                      <Download size={14} /> Export <ChevronDown size={13} className="opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuLabel className="text-[#1D4ED8]">Export documents</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => downloadPdf('plan')} data-testid="npa-export-plan-pdf" className="flex-col items-start gap-0.5 py-2 cursor-pointer">
                      <span className="flex items-center gap-2 font-semibold text-slate-800"><FileText size={13} className="text-[#1D4ED8]" /> 90-Day Plan PDF</span>
                      <span className="text-[11px] text-slate-500 leading-snug">A branded PDF of your full 90-day plan and daily work.</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowPacket(true)} data-testid="npa-export-board-packet" className="flex-col items-start gap-0.5 py-2 cursor-pointer">
                      <span className="flex items-center gap-2 font-semibold text-slate-800"><FileText size={13} className="text-[#7C3AED]" /> Board Packet</span>
                      <span className="text-[11px] text-slate-500 leading-snug">Pick sections, AI-draft any gaps, then download or email a board-ready PDF.</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Primary action */}
                <button type="button" onClick={downloadPlan} data-testid="npa-download-plan" className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold bg-[#1D4ED8] text-white rounded-lg hover:bg-[#1E40AF] hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"><Rocket size={13} /> Launch plan</button>

                <input ref={restoreInputRef} type="file" accept=".json,application/json" className="hidden" onChange={importBackup} data-testid="npa-restore-input" />
              </div>
            </div>

            {/* What each tool does — collapsible help */}
            <Collapsible open={showHelp} onOpenChange={setShowHelp} className="mb-4">
              <CollapsibleTrigger asChild>
                <button type="button" data-testid="npa-help-toggle" className="inline-flex items-center gap-2 text-xs font-semibold text-[#64748B] hover:text-[#0A0F1C] transition-colors">
                  <Info size={14} /> {showHelp ? 'Hide' : 'What each tool does'} <ChevronDown size={13} className={`transition-transform ${showHelp ? 'rotate-180' : ''}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5 p-6 bg-slate-50 border-l-4 border-fuchsia-400 rounded-r-2xl border-y border-r border-slate-200" data-testid="npa-help-panel">
                  {[
                    ['AI Tools', 'Sparkles', 'AI helpers: build a Launch Blueprint, refresh your brand wording, or auto-draft every day for you.'],
                    ['Manage', 'Database', 'Share your progress publicly, review the activity log, and back up or restore the whole project.'],
                    ['Export', 'Download', 'Download a branded 90-Day Plan PDF or a board-ready Board Packet you can email out.'],
                    ['Launch plan', 'Rocket', 'Your one-click, everything-in bundle — the full plan ready to hand off or present.'],
                    ['Describe your nonprofit', 'Wand2', 'Type or speak about your org, or paste a website — AI fills in mission, who you serve, focus & funding.'],
                    ['Board-ready score', 'CheckCircle2', 'A live readiness score. Tap it to see exactly what still needs attention before you launch.'],
                  ].map(([title, , desc]) => (
                    <div key={title} className="space-y-1">
                      <div className="text-xs font-black uppercase tracking-wider text-slate-800">{title}</div>
                      <p className="text-[13px] text-slate-600 leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Launch progress at-a-glance */}
            {(() => {
              const pctDays = Math.round((completedCount / 90) * 100);
              const nextTasks = (curriculum || [])
                .filter((d) => d.type === 'task' && !((active.days || {})[String(d.day)]?.completed))
                .slice(0, 3);
              const score = readiness?.score ?? 0;
              const R = 26, C = 2 * Math.PI * R;
              return (
                <div className="mb-5 bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="npa-launch-progress">
                  <div className="grid grid-cols-1 md:grid-cols-[auto_auto_1fr] gap-5 md:gap-8 items-center">
                    {/* Days ring */}
                    <div className="flex items-center gap-4">
                      <div className="relative w-[68px] h-[68px] shrink-0">
                        <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
                          <circle cx="32" cy="32" r={R} fill="none" stroke="#EEF2F7" strokeWidth="7" />
                          <circle cx="32" cy="32" r={R} fill="none" stroke="#1D4ED8" strokeWidth="7" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C - (C * pctDays) / 100} className="transition-all duration-700" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-base font-black text-[#0A0F1C] leading-none" data-testid="npa-progress-pct">{pctDays}%</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-wider text-[#94A3B8]">Days done</div>
                        <div className="text-lg font-black text-[#0A0F1C]">{completedCount}<span className="text-sm font-bold text-[#94A3B8]">/90</span></div>
                      </div>
                    </div>
                    {/* Board-ready */}
                    <button type="button" onClick={() => readiness && setShowReadiness(true)} data-testid="npa-progress-readiness" className="flex items-center gap-3 text-left md:border-l md:border-[#F1F5F9] md:pl-8 group">
                      <span className={`w-11 h-11 rounded-xl flex items-center justify-center font-black text-sm ${score >= 80 ? 'bg-emerald-50 text-emerald-600' : score >= 40 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-500'}`}>{score}</span>
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-wider text-[#94A3B8]">Board-ready</div>
                        <div className="text-sm font-bold text-[#0A0F1C] group-hover:text-[#1D4ED8] transition-colors">{score >= 90 ? 'Ready to launch' : score >= 80 ? 'Almost there' : score >= 40 ? 'In progress' : 'Just starting'} →</div>
                      </div>
                    </button>
                    {/* Next tasks */}
                    <div className="md:border-l md:border-[#F1F5F9] md:pl-8">
                      <div className="text-[10px] font-black uppercase tracking-wider text-[#94A3B8] mb-1.5">Do next</div>
                      {nextTasks.length > 0 ? (
                        <div className="flex flex-col gap-1" data-testid="npa-next-tasks">
                          {nextTasks.map((d) => (
                            <button type="button" key={d.day} onClick={() => setSelectedDay(d.day)} data-testid={`npa-next-task-${d.day}`} className="flex items-center gap-2 text-left text-sm text-[#334155] hover:text-[#1D4ED8] transition-colors group">
                              <span className="shrink-0 text-[10px] font-black text-[#7C3AED] bg-fuchsia-50 rounded px-1.5 py-0.5">Day {d.day}</span>
                              <span className="truncate group-hover:underline">{d.title}</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm font-bold text-emerald-600 flex items-center gap-1.5"><CheckCircle2 size={15} /> All tasks complete — nicely done!</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}


            {draftingAll && draftAllProgress.current_title && (
              <div className="mb-4 text-xs text-[#64748B] bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg px-3 py-2" data-testid="npa-draft-all-status">
                Drafting Day {draftAllProgress.current_day}: {draftAllProgress.current_title} · {draftAllProgress.completed}/{draftAllProgress.total} done. You can keep working — this runs in the background.
              </div>
            )}

            {/* AI Intake: fill project info by typing or speaking */}
            <div className="mb-5 bg-gradient-to-br from-[#F5F3FF] to-white border border-[#DDD6FE] rounded-2xl p-5" data-testid="npa-intake-panel">
              <div className="flex items-center gap-2 mb-1">
                <Wand2 size={16} className="text-[#7C3AED]" aria-hidden="true" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Describe your nonprofit - type or speak</h2>
              </div>
              <p className="text-xs text-[#64748B] mb-2">Talk or type naturally about your organization. AI will fill in your mission, who you serve, focus areas, funding and more - so you don't have to fill each field by hand.</p>

              {/* Option 1: Pull from a website */}
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5">Option 1 · Paste your website</div>
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} data-testid="npa-website-url" placeholder="Paste a website URL (e.g. GreaterCommunityImpact.org)" className="flex-1 min-w-[220px] px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8] focus:ring-1 focus:ring-[#1D4ED8]" />
                <button type="button" onClick={handleWebsiteIntake} disabled={websiteLoading} data-testid="npa-website-pull" className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold bg-[#1D4ED8] text-white rounded-lg hover:bg-[#1E40AF] disabled:opacity-60 transition-colors">
                  {websiteLoading ? <><RefreshCw size={13} className="animate-spin" /> Pulling…</> : <><Wand2 size={13} /> Pull from website</>}
                </button>
              </div>

              {/* Option 2: Type or speak — unified composite input with an obvious submit + mic */}
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5">Option 2 · Type or speak about it</div>
              <div className="relative border border-slate-300 rounded-xl bg-white focus-within:border-[#1D4ED8] focus-within:ring-1 focus-within:ring-[#1D4ED8] transition-colors overflow-hidden">
                <textarea value={intakeText} onChange={(e) => setIntakeText(e.target.value)} data-testid="npa-intake-text" rows={4}
                  placeholder="e.g. We help single moms in Savannah get back to work with resume workshops and childcare during interviews. We're funded by grants and donations..."
                  className="w-full border-0 bg-transparent focus:outline-none resize-none p-4 text-sm text-slate-800 placeholder:text-slate-400" />
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 border-t border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-2">
                    {!recording ? (
                      <button type="button" onClick={startRecording} disabled={transcribing} data-testid="npa-record-start" title="Record your voice — AI will transcribe it into the box" className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-[#DDD6FE] text-[#7C3AED] hover:bg-fuchsia-50 hover:border-fuchsia-300 disabled:opacity-60 transition-colors">
                        {transcribing ? <RefreshCw size={15} className="animate-spin" /> : <Mic size={16} />}
                      </button>
                    ) : (
                      <button type="button" onClick={stopRecording} data-testid="npa-record-stop" title="Stop recording & transcribe" className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-bold bg-red-600 text-white rounded-full animate-pulse">
                        <Square size={13} /> Stop &amp; transcribe
                      </button>
                    )}
                    <label className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-bold bg-white border border-[#DDD6FE] text-[#7C3AED] rounded-full hover:bg-fuchsia-50 cursor-pointer transition-colors" data-testid="npa-upload-audio-label" title="Upload an existing audio/video recording">
                      <Upload size={14} /> Upload audio
                      <input ref={audioFileRef} type="file" accept="audio/*,video/mp4,.m4a,.mp3,.wav,.webm" onChange={handleAudioUpload} className="hidden" data-testid="npa-upload-audio-input" />
                    </label>
                    <span className="hidden sm:inline text-[11px] text-slate-400">Type or tap the mic, then submit →</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {lastUpdated.length > 0 && <span className="text-[11px] font-semibold text-emerald-600" data-testid="npa-intake-updated">Updated: {lastUpdated.join(', ')}</span>}
                    <button type="button" onClick={handleAutofillDays} disabled={autofilling || !intakeText.trim()} data-testid="npa-autofill-days" title="Distribute this across all 90 days of the plan" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 disabled:opacity-50 transition-colors">
                      {autofilling ? <><RefreshCw size={13} className="animate-spin" /> Auto-filling…</> : <><Rocket size={13} /> Auto-fill all 90 days</>}
                    </button>
                    <button type="button" onClick={handleIntake} disabled={intaking || !intakeText.trim()} data-testid="npa-intake-submit" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-[#F97316] text-white rounded-lg hover:bg-[#EA580C] disabled:opacity-50 hover:shadow-md transition-all duration-200">
                      {intaking ? <><RefreshCw size={13} className="animate-spin" /> Analyzing…</> : <><Send size={13} /> Analyze &amp; fill my info</>}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-2 mt-3">
                {[['mission', 'Mission'], ['who_served', 'Who it serves'], ['focus_areas', 'Focus areas'], ['community', 'Community'], ['tagline', 'Tagline'], ['elevator_pitch', 'Elevator pitch']].map(([k, label]) => (
                  active[k] ? (
                    <div key={k} className="text-xs bg-white/70 border border-[#EDE9FE] rounded-lg p-2" data-testid={`npa-info-${k}`}>
                      <span className="font-bold text-[#7C3AED]">{label}: </span><span className="text-[#0A0F1C]">{active[k]}</span>
                    </div>
                  ) : null
                ))}
              </div>

              {/* Sync from a completed 7-Stage Jumpstart (nonprofit) session */}
              <div className="mt-3 border-t border-[#EDE9FE] pt-3" data-testid="npa-sync-session">
                <div className="flex items-center gap-2 mb-1">
                  <Users size={14} className="text-[#7C3AED]" aria-hidden="true" />
                  <span className="text-[11px] font-black uppercase tracking-wider text-[#0A0F1C]">Sync from a 7-Stage session</span>
                </div>
                <p className="text-xs text-[#64748B] mb-2">Turn a completed 7-Stage Jumpstart consultation into a head start: AI distributes that session's answers &amp; blueprint into the matching days as pre-filled notes (nothing is overwritten).</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={syncSessionId} onChange={(e) => setSyncSessionId(e.target.value)} data-testid="npa-sync-session-select" className="flex-1 min-w-[220px] px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                    <option value="">{npaSessions.length ? 'Choose a nonprofit session…' : 'No nonprofit sessions yet — create one in 7-Stage Sessions'}</option>
                    {npaSessions.map((s) => (
                      <option key={s.session_id} value={s.session_id}>
                        {(s.client_name || s.business_name || 'Session')} · {s.has_deliverable ? 'blueprint ready' : `${s.answer_count} answers`}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={handleSyncFromSession} disabled={syncing || !syncSessionId} data-testid="npa-sync-session-run" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#7C3AED] text-white rounded-lg hover:opacity-90 disabled:opacity-60">
                    {syncing ? <><RefreshCw size={13} className="animate-spin" /> Pre-filling…</> : <><Wand2 size={13} /> Sync &amp; pre-fill 90 days</>}
                  </button>
                </div>
              </div>
            </div>

            {/* Board Recruitment Tracker */}
            {/* Launch Blueprint modal */}
            {showBlueprint && active.blueprint && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="npa-blueprint-modal" onClick={() => setShowBlueprint(false)}>
                <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold text-[#0A0F1C] flex items-center gap-2"><Wand2 size={18} className="text-[#7C3AED]" /> Launch Blueprint</h2>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => { navigator.clipboard.writeText(active.blueprint); toast.success('Copied'); }} data-testid="npa-blueprint-copy" className="text-[11px] font-bold text-[#1D4ED8] hover:underline">Copy</button>
                      <button type="button" onClick={() => { const b = new Blob([active.blueprint], { type: 'text/plain' }); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `${active.name}-blueprint.txt`; a.click(); URL.revokeObjectURL(u); }} data-testid="npa-blueprint-download" className="text-[11px] font-bold text-[#1D4ED8] hover:underline">.txt</button>
                      <button type="button" onClick={() => window.open(`${API_URL}/api/admin/nonprofit-accelerator/projects/${active.project_id}/blueprint-pdf`, '_blank')} data-testid="npa-blueprint-pdf" className="text-[11px] font-bold text-[#7C3AED] hover:underline">PDF</button>
                      <button type="button" onClick={() => setShowBlueprint(false)} data-testid="npa-blueprint-close" aria-label="Close" className="text-[#64748B] hover:text-[#0A0F1C]"><ChevronLeft size={18} /></button>
                    </div>
                  </div>
                  <div className="text-sm text-[#0A0F1C] whitespace-pre-wrap">{active.blueprint}</div>
                </div>
              </div>
            )}

            {/* AI Brand Refresh modal */}
            {showBrand && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="npa-brand-modal" onClick={() => setShowBrand(false)}>
                <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-lg font-bold text-[#0A0F1C] flex items-center gap-2"><Palette size={18} className="text-fuchsia-600" /> AI Brand Refresh</h2>
                    <div className="flex items-center gap-3">
                      {(active.brand_versions || []).length > 0 && (
                        <button type="button" onClick={revertBrand} data-testid="npa-brand-revert" className="text-[11px] font-bold text-[#1D4ED8] hover:underline">Revert last</button>
                      )}
                      <button type="button" onClick={handleBrandRefresh} disabled={brandLoading} data-testid="npa-brand-regenerate" className="text-[11px] font-bold text-fuchsia-700 hover:underline disabled:opacity-50">Regenerate</button>
                      <button type="button" onClick={() => setShowBrand(false)} data-testid="npa-brand-close" aria-label="Close" className="text-[#64748B] hover:text-[#0A0F1C]"><ChevronLeft size={18} /></button>
                    </div>
                  </div>
                  <p className="text-xs text-[#64748B] mb-4">Pick the wording that connects best with your community. Applying saves your current wording first, so you can always revert.{active.brand_options_at ? ' Showing your latest generated options — click Regenerate for fresh ones.' : ''}</p>
                  <div className="space-y-5">
                    {['mission', 'vision', 'tagline', 'elevator_pitch'].map((field) => {
                      const opts = (brandOptions || {})[field];
                      if (!opts) return null;
                      const tones = [['warm', 'Warm'], ['formal', 'Formal'], ['bold', 'Bold']];
                      return (
                        <div key={field} data-testid={`npa-brand-field-${field}`} className="border border-[#E2E8F0] rounded-xl p-4">
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="text-xs font-black uppercase tracking-wider text-[#0A0F1C]">{BRAND_LABELS[field]}</div>
                            {brandSel[field] && <button type="button" onClick={() => setBrandSel((s) => { const n = { ...s }; delete n[field]; return n; })} data-testid={`npa-brand-clear-${field}`} className="text-[10px] font-bold text-[#94A3B8] hover:text-red-500">Clear</button>}
                          </div>
                          {active[field] ? (
                            <div className="text-[11px] text-[#94A3B8] mb-2 italic">Current: {active[field]}</div>
                          ) : null}
                          <div className="grid gap-2">
                            {tones.map(([tone, tlabel]) => opts[tone] ? (
                              <button
                                type="button"
                                key={tone}
                                onClick={() => setBrandSel((s) => ({ ...s, [field]: opts[tone] }))}
                                data-testid={`npa-brand-opt-${field}-${tone}`}
                                className={`text-left rounded-lg border p-2.5 transition-colors ${brandSel[field] === opts[tone] ? 'border-fuchsia-500 bg-fuchsia-50' : 'border-[#E2E8F0] hover:border-fuchsia-300'}`}
                              >
                                <span className="inline-block text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full mb-1 bg-[#F1F5F9] text-[#475569]">{tlabel}</span>
                                <div className="text-sm text-[#0A0F1C]">{opts[tone]}</div>
                              </button>
                            ) : null)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-[#F1F5F9] flex-wrap">
                    <span className="text-xs text-[#94A3B8] mr-auto">{Object.keys(brandSel).length} selected</span>
                    {(active.mission || active.vision || active.tagline || active.elevator_pitch) && (
                      <>
                        <button type="button" onClick={downloadVoiceGuide} data-testid="npa-brand-voice-guide" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#1D4ED8] text-[#1D4ED8] rounded-lg hover:bg-[#EFF6FF]"><Download size={13} /> Brand voice guide (PDF)</button>
                        <button type="button" onClick={matchVoiceDeliverables} disabled={voiceMatching} data-testid="npa-brand-match-voice" title="Rewrite your landing-page copy (Day 36) and leadership bio (Day 59) to match this voice" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-fuchsia-300 text-fuchsia-700 rounded-lg hover:bg-fuchsia-50 disabled:opacity-60">
                          {voiceMatching ? <><RefreshCw size={13} className="animate-spin" /> Matching…</> : <><Wand2 size={13} /> Match voice everywhere</>}
                        </button>
                      </>
                    )}
                    <button type="button" onClick={() => setShowBrand(false)} className="px-4 py-2 text-sm font-bold text-[#475569] hover:text-[#0A0F1C]">Cancel</button>
                    <button type="button" onClick={applyBrand} disabled={applyingBrand || !Object.keys(brandSel).length} data-testid="npa-brand-apply" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-fuchsia-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50">
                      {applyingBrand ? <><RefreshCw size={14} className="animate-spin" /> Applying…</> : <><CheckCircle2 size={14} /> Apply selected</>}
                    </button>
                  </div>
                </div>
              </div>
            )}


            <div className="mb-5 bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="npa-board-tracker">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C] flex items-center gap-2"><ClipboardList size={16} className="text-[#1D4ED8]" /> Board Recruitment Tracker <span className="text-[#94A3B8] font-normal normal-case">({prospects.length})</span></h2>
                <button type="button" onClick={suggestProspects} disabled={suggesting} data-testid="npa-suggest-prospects" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 rounded-lg hover:bg-fuchsia-100 disabled:opacity-60">
                  {suggesting ? <><RefreshCw size={12} className="animate-spin" /> Suggesting…</> : <><Sparkles size={12} /> AI: suggest prospects</>}
                </button>
              </div>

              {prospects.length > 0 && (
                <div className="overflow-x-auto mb-3">
                  <table className="w-full text-sm" data-testid="npa-prospect-table">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-[#94A3B8] text-left border-b border-[#F1F5F9]">
                        <th className="py-1.5 pr-2">Name</th><th className="py-1.5 pr-2">Skill / role</th><th className="py-1.5 pr-2">Status</th><th className="py-1.5 pr-2">Notes</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {prospects.map((pr) => (
                        <tr key={pr.prospect_id} data-testid={`npa-prospect-${pr.prospect_id}`} className="border-b border-[#F8FAFC]">
                          <td className="py-1.5 pr-2"><input value={pr.name || ''} onChange={(e) => updateProspect(pr.prospect_id, { name: e.target.value })} placeholder="Add name" className="w-28 px-1.5 py-1 border border-transparent hover:border-[#E2E8F0] focus:border-[#1D4ED8] rounded text-sm" /></td>
                          <td className="py-1.5 pr-2"><input value={pr.skill || ''} onChange={(e) => updateProspect(pr.prospect_id, { skill: e.target.value })} className="w-36 px-1.5 py-1 border border-transparent hover:border-[#E2E8F0] focus:border-[#1D4ED8] rounded text-sm" /></td>
                          <td className="py-1.5 pr-2">
                            <select value={pr.status || 'prospect'} onChange={(e) => updateProspect(pr.prospect_id, { status: e.target.value })} data-testid={`npa-prospect-status-${pr.prospect_id}`} className={`px-2 py-1 rounded-full text-[11px] font-bold border-none ${STATUS_COLORS[pr.status] || STATUS_COLORS.prospect}`}>
                              {PROSPECT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                          <td className="py-1.5 pr-2"><input value={pr.notes || ''} onChange={(e) => updateProspect(pr.prospect_id, { notes: e.target.value })} className="w-full min-w-[160px] px-1.5 py-1 border border-transparent hover:border-[#E2E8F0] focus:border-[#1D4ED8] rounded text-xs text-[#475569]" /></td>
                          <td className="py-1.5"><button type="button" onClick={() => deleteProspect(pr.prospect_id)} aria-label="Delete" className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-end gap-2 flex-wrap border-t border-[#F1F5F9] pt-3">
                <input value={newProspect.name} onChange={(e) => setNewProspect({ ...newProspect, name: e.target.value })} placeholder="Name" data-testid="npa-new-prospect-name" className="px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-sm w-32" />
                <input value={newProspect.skill} onChange={(e) => setNewProspect({ ...newProspect, skill: e.target.value })} placeholder="Skill / role" data-testid="npa-new-prospect-skill" className="px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-sm w-40" />
                <button type="button" onClick={addProspect} data-testid="npa-add-prospect" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90"><Plus size={13} /> Add prospect</button>
                {prospects.length === 0 && <span className="text-xs text-[#94A3B8]">Build your board here, or let AI suggest the profiles you need.</span>}
              </div>
            </div>


            {/* Phase progress */}
            <div className="mb-4 grid sm:grid-cols-3 gap-3" data-testid="npa-phase-bar">
              {phases.map((ph) => {
                const pdays = curriculum.filter((d) => d.phase === ph.phase);
                const done = pdays.filter((d) => (active.days || {})[String(d.day)]?.completed).length;
                const pct = pdays.length ? Math.round((done / pdays.length) * 100) : 0;
                const isActive = spec.phase === ph.phase;
                return (
                  <div key={ph.phase} data-testid={`npa-phase-${ph.phase}`} className={`rounded-xl border p-3 ${isActive ? 'border-[#1D4ED8] bg-[#EFF6FF]' : 'border-[#E2E8F0] bg-white'}`}>
                    <div className="text-[12px] font-black text-[#0A0F1C]">{ph.title}</div>
                    <div className="text-[10px] text-[#64748B] mb-1.5">Days {ph.start}-{ph.end} · {ph.subtitle}</div>
                    <div className="h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden"><div className="h-full bg-[#1D4ED8] rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
                    <div className="text-[10px] text-[#94A3B8] mt-1">{done}/{pdays.length} days complete</div>
                  </div>
                );
              })}
            </div>

            {/* View switcher */}
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-white p-1" data-testid="npa-view-switcher">
                {[['roadmap', 'Roadmap', MapIcon], ['calendar', 'Calendar', CalendarDays], ['list', 'List', ClipboardList]].map(([v, label, Icon]) => (
                  <button key={v} type="button" onClick={() => setPlanView(v)} data-testid={`npa-view-${v}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md ${planView === v ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}><Icon size={13} /> {label}</button>
                ))}
              </div>
              <span className="text-xs font-semibold text-[#64748B]" data-testid="npa-selected-day-label">Selected · Day {spec.day}: {spec.title}</span>
            </div>

            {/* Browse: roadmap / calendar / list */}
            <div className="mb-5" data-testid="npa-browse">
              {planView === 'list' ? (
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-3 max-h-[60vh] overflow-y-auto" data-testid="npa-day-rail">
                  {phases.map((ph) => (
                    <div key={ph.phase} className="mb-3">
                      <div className="text-[10px] font-black uppercase tracking-wider text-[#1D4ED8] px-2.5 py-1">{ph.title} <span className="text-[#94A3B8] font-bold">· Days {ph.start}-{ph.end}</span></div>
                      {curriculum.filter((d) => d.phase === ph.phase).map((d) => {
                        const done = (active.days || {})[String(d.day)]?.completed;
                        const meta = TYPE_META[d.type] || TYPE_META.task;
                        const Icon = done ? CheckCircle2 : (d.day === selectedDay ? meta.icon : Circle);
                        return (
                          <button key={d.day} type="button" onClick={() => selectDay(d.day)} data-testid={`npa-day-${d.day}`}
                            className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg mb-0.5 ${d.day === selectedDay ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'}`}>
                            <Icon size={15} style={{ color: done ? '#059669' : meta.color }} aria-hidden="true" />
                            <span className="text-[13px] font-semibold text-[#0A0F1C] flex-1 truncate">Day {d.day}: {d.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ) : planView === 'calendar' ? (
                <div className="space-y-4">
                  {phases.map((ph) => (
                    <div key={ph.phase} className="bg-white border border-[#E2E8F0] rounded-2xl p-4" data-testid={`npa-calendar-phase-${ph.phase}`}>
                      <div className="text-xs font-black text-[#0A0F1C] mb-2">{ph.title} <span className="font-normal text-[#94A3B8]">· Days {ph.start}-{ph.end}</span></div>
                      <div className="grid grid-cols-7 gap-1.5">
                        {curriculum.filter((d) => d.phase === ph.phase).map((d) => {
                          const done = (active.days || {})[String(d.day)]?.completed;
                          const meta = TYPE_META[d.type] || TYPE_META.task;
                          const selected = d.day === selectedDay;
                          return (
                            <button key={d.day} type="button" onClick={() => selectDay(d.day)} data-testid={`npa-cal-day-${d.day}`} title={`Day ${d.day}: ${d.title}`}
                              className={`aspect-square rounded-lg border p-1 flex flex-col items-center justify-center text-center overflow-hidden ${selected ? 'border-[#1D4ED8] ring-1 ring-[#1D4ED8] bg-[#EFF6FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1] bg-white'}`}
                              style={{ borderLeft: `3px solid ${done ? '#059669' : meta.color}` }}>
                              <span className="text-[12px] font-black text-[#0A0F1C] leading-none">{d.day}</span>
                              <span className="text-[8px] text-[#64748B] leading-tight mt-0.5 line-clamp-2">{d.title}</span>
                              {done && <CheckCircle2 size={10} className="text-emerald-600 mt-0.5" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-5">
                  {phases.map((ph) => (
                    <div key={ph.phase} data-testid={`npa-roadmap-phase-${ph.phase}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1D4ED8] text-white text-[12px] font-black shrink-0">{ph.phase}</span>
                        <div>
                          <div className="text-sm font-black text-[#0A0F1C]">{ph.title}</div>
                          <div className="text-[10px] text-[#64748B]">Days {ph.start}-{ph.end} · {ph.subtitle}</div>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:pl-9">
                        {curriculum.filter((d) => d.phase === ph.phase).map((d) => {
                          const done = (active.days || {})[String(d.day)]?.completed;
                          const meta = TYPE_META[d.type] || TYPE_META.task;
                          const Icon = done ? CheckCircle2 : meta.icon;
                          const selected = d.day === selectedDay;
                          return (
                            <button key={d.day} type="button" onClick={() => selectDay(d.day)} data-testid={`npa-day-${d.day}`}
                              className={`text-left rounded-xl border p-2.5 transition-all hover:shadow-sm ${selected ? 'border-[#1D4ED8] bg-[#EFF6FF] ring-1 ring-[#1D4ED8]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: done ? '#059669' : meta.color }}>Day {d.day}</span>
                                <Icon size={14} style={{ color: done ? '#059669' : meta.color }} aria-hidden="true" />
                              </div>
                              <div className="text-[13px] font-semibold text-[#0A0F1C] leading-tight">{d.title}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Day detail */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="npa-day-detail">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: (TYPE_META[spec.type] || TYPE_META.task).color }}>{(TYPE_META[spec.type] || TYPE_META.task).label} · Day {spec.day}</div>
                    <h2 className="text-xl font-bold text-[#0A0F1C]">{spec.title}</h2>
                  </div>
                  <button type="button" onClick={toggleComplete} disabled={savingDay} data-testid="npa-toggle-complete"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg ${dayEntry.completed ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-[#F1F5F9] text-[#475569]'}`}>
                    {dayEntry.completed ? <CheckCircle2 size={13} /> : <Circle size={13} />} {dayEntry.completed ? 'Completed' : 'Mark complete'}
                  </button>
                </div>

                {spec.tip && <div className="bg-[#FEF9C3] border border-[#FDE68A] rounded-lg p-3 text-sm text-[#854D0E]"><strong>Today's focus:</strong> {spec.tip}</div>}
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 text-sm text-[#0A0F1C]"><strong>Your task:</strong> {spec.task}</div>
                {spec.asset && (
                  <div className="flex items-center gap-2 text-xs text-[#64748B]" data-testid="npa-asset">
                    <FileText size={13} className="text-[#94A3B8]" /> Program template: <strong>{spec.asset}</strong> <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Provided in program</span>
                  </div>
                )}

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Your work / notes</label>
                  <textarea value={dayNotes} onChange={(e) => setDayNotes(e.target.value)} data-testid="npa-day-notes" rows={6} placeholder="Capture your answers, decisions, and work for this day..." className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <button type="button" onClick={() => saveDay()} disabled={savingDay} data-testid="npa-save-day" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90 disabled:opacity-60">{savingDay ? <RefreshCw size={12} className="animate-spin" /> : null} Save</button>
                    {spec.ai && (
                      <button type="button" onClick={handleAiDraft} disabled={drafting} data-testid="npa-ai-draft" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 rounded-lg hover:bg-fuchsia-100 disabled:opacity-60">
                        {drafting ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />} {drafting ? 'Drafting…' : 'Help me do this (AI)'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Source material: upload files + notes that AI-draft will incorporate */}
                <div data-testid="npa-day-attachments">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Source material (uploads)</label>
                  <p className="text-[11px] text-[#64748B] mb-2">Upload an existing draft, form, or notes (PDF, Word, or text). The AI draft will read it, combine it with your notes above, and craft a polished, professional version.</p>
                  {(dayEntry.attachments || []).length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {(dayEntry.attachments || []).map((a) => (
                        <div key={a.id} data-testid={`npa-attachment-${a.id}`} className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2.5 py-1.5">
                          <FileText size={13} className="text-[#1D4ED8] shrink-0" />
                          <a href={`${API_URL}/api/admin/nonprofit-accelerator/projects/${active.project_id}/days/${selectedDay}/attachments/${a.id}/download`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#0A0F1C] truncate flex-1 hover:underline">{a.name}</a>
                          <button type="button" onClick={() => deleteDayAttachment(a.id)} data-testid={`npa-attachment-delete-${a.id}`} aria-label="Remove" className="text-[#94A3B8] hover:text-red-500"><Trash2 size={13} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label data-testid="npa-attachment-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-dashed border-[#CBD5E1] text-[#475569] rounded-lg hover:bg-[#F8FAFC] cursor-pointer">
                    <Upload size={13} /> Upload a file
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.md" onChange={uploadDayAttachment} />
                  </label>
                  <button type="button" onClick={openVersions} data-testid="npa-day-history" className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-[#E2E8F0] text-[#475569] rounded-lg hover:bg-[#F8FAFC]"><RefreshCw size={13} /> Version history</button>
                </div>

                  <div className="mt-4 rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white" data-testid="npa-ai-output">
                    <div className="h-1.5 w-full bg-gradient-to-r from-[#1D4ED8] via-fuchsia-500 to-[#F97316]" aria-hidden="true" />
                    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/70">
                      <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Sparkles size={15} className="text-fuchsia-500" /> AI-generated draft</h4>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => { navigator.clipboard.writeText(dayEntry.ai_draft); toast.success('Copied'); }} data-testid="npa-ai-copy" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold border border-slate-200 text-slate-600 bg-white rounded-lg hover:bg-slate-50 transition-colors"><Copy size={12} /> Copy</button>
                        <button type="button" onClick={() => { setDayNotes((n) => (n ? n + '\n\n' : '') + dayEntry.ai_draft); toast.success('Added to your notes'); }} data-testid="npa-ai-use-notes" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold border border-[#BFDBFE] text-[#1D4ED8] bg-[#EFF6FF] rounded-lg hover:bg-[#DBEAFE] transition-colors"><ClipboardList size={12} /> Use as my notes</button>
                      </div>
                    </div>
                    <div className="p-5 max-h-[28rem] overflow-y-auto">
                      <FormattedDraft text={dayEntry.ai_draft} />
                    </div>
                  </div>
                )}
              </div>
          </div>
        )}
      </div>
      {showPacket && active && (
        <BoardPacketModal projectId={active.project_id} projectName={active.name} onClose={() => setShowPacket(false)} />
      )}
      {showVersions && active && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="npa-versions-modal" onClick={() => setShowVersions(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-black text-[#0A0F1C] mb-1">Version history · Day {selectedDay}</h3>
            <p className="text-xs text-[#64748B] mb-3">Every edit, AI draft, polish or restore is saved here (last 10 or 30 days). Restore any version - your current one is saved first.</p>
            {versions.length === 0 ? (
              <p className="text-sm text-[#94A3B8] py-6 text-center">No previous versions yet.</p>
            ) : (
              <div className="space-y-2">
                {versions.map((v) => (
                  <div key={v.id} data-testid={`npa-version-${v.id}`} className="border border-[#E2E8F0] rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-black uppercase tracking-wider text-[#7C3AED]">{(v.source || 'edit').replace(/-/g, ' ')}</span>
                      <span className="text-[10px] text-[#94A3B8]">{new Date(v.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-[#475569] line-clamp-2">{v.draft_preview || v.notes_preview || '(empty)'}</p>
                    <div className="mt-2 flex items-center gap-3">
                      <button type="button" onClick={() => setCmpVer(cmpVer && cmpVer.id === v.id ? null : v)} data-testid={`npa-version-compare-${v.id}`} className="text-xs font-bold text-[#475569] hover:underline">{cmpVer && cmpVer.id === v.id ? 'Hide compare' : 'Compare'}</button>
                      <button type="button" onClick={() => restoreVersion(v.id)} data-testid={`npa-version-restore-${v.id}`} className="text-xs font-bold text-[#1D4ED8] hover:underline">Restore this version</button>
                    </div>
                    {cmpVer && cmpVer.id === v.id && (
                      <div className="mt-2 grid grid-cols-2 gap-2" data-testid="npa-version-compare-panel">
                        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-2">
                          <div className="text-[10px] font-black uppercase text-[#7C3AED] mb-1">This version</div>
                          <div className="text-[11px] text-[#475569] whitespace-pre-wrap max-h-52 overflow-y-auto">{v.draft_preview || v.notes_preview || '(empty)'}</div>
                        </div>
                        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded p-2">
                          <div className="text-[10px] font-black uppercase text-[#1D4ED8] mb-1">Current</div>
                          <div className="text-[11px] text-[#475569] whitespace-pre-wrap max-h-52 overflow-y-auto">{(dayEntry.ai_draft || dayEntry.notes || '(empty)').slice(0, 800)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {showReadiness && readiness && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="npa-readiness-modal" onClick={() => setShowReadiness(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-black text-[#0A0F1C] mb-1">Board readiness · {readiness.score}%</h3>
            <p className="text-xs text-[#64748B] mb-3">Your board-critical pieces. Tap "Go" to jump to the right day and complete it.</p>
            <div className="space-y-2">
              {readiness.items.map((it) => (
                <div key={it.key} data-testid={`npa-readiness-item-${it.key}`} className="flex items-center gap-2 border border-[#E2E8F0] rounded-lg px-3 py-2">
                  {it.done ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Circle size={16} className="text-red-400" />}
                  <span className="text-sm font-semibold text-[#0A0F1C] flex-1">{it.label}</span>
                  {!it.done && READINESS_DAY[it.key] && (
                    <button type="button" onClick={() => { setMode('accelerator'); selectDay(READINESS_DAY[it.key]); setShowReadiness(false); }} data-testid={`npa-readiness-go-${it.key}`} className="text-xs font-bold text-[#1D4ED8] hover:underline">Go →</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showActivity && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="npa-activity-modal" onClick={() => setShowActivity(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-black text-[#0A0F1C] mb-1">Activity log</h3>
            <p className="text-xs text-[#64748B] mb-3">A timeline of changes to this project — who did what, and when.</p>
            {activity.length === 0 ? (
              <p className="text-sm text-[#94A3B8] py-6 text-center">No activity recorded yet.</p>
            ) : (
              <div className="space-y-2">
                {activity.map((a) => (
                  <div key={a.id} className="flex items-start gap-2 border-b border-[#F1F5F9] pb-2">
                    <span className="text-[10px] font-black uppercase tracking-wider text-[#7C3AED] mt-0.5">{a.action}</span>
                    <div className="flex-1">
                      <div className="text-xs text-[#0A0F1C]">{a.detail}</div>
                      <div className="text-[10px] text-[#94A3B8]">{a.actor} · {new Date(a.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
